// A user's MEMBERSHIP lifecycle on a scope (org / game / build), as stored in their
// UserGameRegistry (orgUserStates + gameDetailsMap[...].state + clientIdDetailsMap[...].state).
//
// This is distinct from gameLifecycle.js, which models a game's CONTENT lifecycle (the game
// itself being SUSPENDED/SUNSET). Here we answer: "can THIS user currently use THIS build?"
//
// Mirrors the backend UserPermissionApplicationService.ACCESS_DENYING_STATES — a SUSPENDED or
// ARCHIVED membership denies all access to that scope (and cascades down), so resolving effective
// permissions for it yields NO_ACCESS. Picking such a build as the active context bounces the user
// to /not-authorized even when they still have other, usable builds — these helpers keep that from
// happening by steering selection toward a build the user can actually open.

const MEMBERSHIP_DENYING_STATES = ['SUSPENDED', 'ARCHIVED'];

const norm = (v) => (v == null ? null : String(v));

/** The denying state a membership sits in (normalised SUSPENDED / ARCHIVED), or null when it still
 *  grants access. Gating callers want isMembershipDenyingState; the /not-authorized page needs the
 *  state itself, because suspend and archive read differently ("suspended" vs "archived"). */
export function denyingStateOf(state) {
    const s = state == null ? '' : String(state).toUpperCase();
    return MEMBERSHIP_DENYING_STATES.includes(s) ? s : null;
}

/** True when a membership state denies access to its scope (SUSPENDED / ARCHIVED). */
export function isMembershipDenyingState(state) {
    return !!denyingStateOf(state);
}

/**
 * A user's ORG-membership state code for `orgId`, from the `orgUserStates` map the user registry
 * returns. The value may arrive as a plain string ("ARCHIVED") or as an object carrying `code`/`name`
 * depending on how the backend State enum serialised, so normalise both.
 */
export function orgMembershipStateOf(user, orgId) {
    const raw = (user && orgId && user.orgUserStates) ? user.orgUserStates[orgId] : null;
    if (raw == null) return null;
    return typeof raw === 'string' ? raw : (raw.code || raw.name || null);
}

/**
 * True when `user` is SUSPENDED/ARCHIVED in `orgId`, and so must not be offered as the TARGET of an
 * access grant, role change or ownership transfer. The PDP denies them the whole org subtree, and the
 * PAP now rejects such a command outright (SUBJECT_NOT_ACTIVE), so listing them is a dead end that
 * ends in an error toast.
 *
 * Absent state means NOT denied — a person with no membership row in this org yet is a legitimate
 * invite target, and this must not become a de-facto org-membership filter.
 */
export function isUserDeniedInOrg(user, orgId) {
    return isMembershipDenyingState(orgMembershipStateOf(user, orgId));
}

/** The game entry in a registry that owns `clientId`, regardless of whether the map is keyed by
 *  gameName or gameId. Falls back to a direct gameId key match when the client isn't found. */
function findGameEntry(gameDetailsMap, gameId, clientId) {
    if (!gameDetailsMap) return null;
    if (clientId) {
        const owner = Object.values(gameDetailsMap).find((e) =>
            e?.clientIdDetailsMap &&
            Object.keys(e.clientIdDetailsMap).some((k) => norm(k) === norm(clientId)));
        if (owner) return owner;
    }
    return gameDetailsMap[gameId]
        || Object.entries(gameDetailsMap).find(([k]) => norm(k) === norm(gameId))?.[1]
        || null;
}

/**
 * The client ids the current user is suspended/archived on for the given game — used to grey out
 * (and block selection of) those builds in the switcher and to keep auto-select away from them.
 * A whole-game membership denial marks every one of that game's clients as suspended.
 */
export function suspendedClientIdsForGame(registry, gameKey, gameId) {
    const out = new Set();
    const gd = registry?.gameDetailsMap;
    if (!gd) return out;
    const entry = gd[gameKey] || findGameEntry(gd, gameId, null);
    const clients = entry?.clientIdDetailsMap;
    if (entry && isMembershipDenyingState(entry.state)) {
        Object.keys(clients || {}).forEach((cid) => out.add(norm(cid)));
        return out;
    }
    if (clients) {
        for (const [cid, details] of Object.entries(clients)) {
            if (isMembershipDenyingState(details?.state)) out.add(norm(cid));
        }
    }
    return out;
}

/**
 * True when the user has at least one SUSPENDED/ARCHIVED membership (game or build) and NO usable
 * (non-denying) granted access anywhere in the supplied registries — i.e. they're locked out
 * *because* a membership was suspended, not merely because they lack a permission.
 *
 * Used by the /not-authorized page to show an "Access Suspended" message instead of the generic
 * "you don't have permission" copy. ORG-level suspension is detected separately (orgState on
 * userDetails.orgs); this covers the game/build-level case — e.g. a Developer who keeps active org
 * membership but whose only build access has been suspended at the client level.
 *
 * @param registries list of UserGameRegistryDTO (one per org)
 */
export function isSuspendedMembershipLockout(registries) {
    let sawDenying = false;
    let sawUsable = false;
    (registries || []).forEach((reg) => {
        const gd = reg?.gameDetailsMap;
        if (!gd) return;
        Object.values(gd).forEach((entry) => {
            // A whole-game membership denial suspends every build under it.
            const gameDenied = isMembershipDenyingState(entry?.state);
            if (gameDenied) sawDenying = true;
            const clients = entry?.clientIdDetailsMap;
            if (clients && Object.keys(clients).length) {
                for (const details of Object.values(clients)) {
                    if (gameDenied || isMembershipDenyingState(details?.state)) sawDenying = true;
                    else sawUsable = true;
                }
            } else if (!gameDenied) {
                // Game-level / owner grant with no per-client rows and not denied → usable.
                sawUsable = true;
            }
        });
    });
    return sawDenying && !sawUsable;
}

/**
 * The game/build scopes the user is SUSPENDED/ARCHIVED on, across all supplied registries. Used by
 * the /not-authorized page to name exactly what was suspended. The gameDetailsMap key is the game's
 * display name in this system (DashboardHeader keys by gameName), so it doubles as a label; client
 * ids are the 8-digit build codes (the registry carries no client name — the caller can resolve
 * friendlier names from the game catalogue if desired).
 *
 * @param registries list of UserGameRegistryDTO (one per org)
 * @returns Array<{ orgId, gameKey, gameId, clientIds: string[], wholeGame: boolean,
 *                  state: 'SUSPENDED'|'ARCHIVED'|'MIXED' }>
 */
export function suspendedScopeDetails(registries) {
    const out = [];
    (registries || []).forEach((reg) => {
        const gd = reg?.gameDetailsMap;
        if (!gd) return;
        Object.entries(gd).forEach(([gameKey, entry]) => {
            const wholeGameState = denyingStateOf(entry?.state);
            const wholeGame = !!wholeGameState;
            const clients = entry?.clientIdDetailsMap || {};
            const clientIds = wholeGame
                ? Object.keys(clients)
                : Object.entries(clients)
                    .filter(([, d]) => isMembershipDenyingState(d?.state))
                    .map(([cid]) => cid);
            if (wholeGame || clientIds.length) {
                // Which state denied this scope, so the caller can say "archived" rather than always
                // "suspended". Game User Management only ever suspends here (its "remove" deletes the
                // grant outright), so ARCHIVED/MIXED at this level is legacy or backend-set data.
                const states = wholeGame
                    ? [wholeGameState]
                    : [...new Set(clientIds.map((cid) => denyingStateOf(clients[cid]?.state)))];
                out.push({
                    orgId: reg.orgId,
                    gameKey,
                    gameId: entry?.gameId || gameKey,
                    clientIds,
                    wholeGame,
                    state: states.length === 1 ? states[0] : 'MIXED',
                });
            }
        });
    });
    return out;
}

/**
 * Resolves a build the user can currently open for an org+game context: the preferred (persisted)
 * build if it's granted and not suspended, otherwise the first granted, non-suspended build.
 * Returns null when none is usable (whole game/org membership suspended, or no grants) so the
 * caller can fall through to the normal auto-select cascade or a suspended-state page.
 *
 * @param registries        list of UserGameRegistryDTO (one per org)
 * @param orgId             active org
 * @param gameId            active game (registry id)
 * @param preferredClientId the persisted/last-used build to keep if still usable
 */
export function pickUsableClientId(registries, orgId, gameId, preferredClientId) {
    const reg = (registries || []).find((r) => norm(r.orgId) === norm(orgId));
    const gd = reg?.gameDetailsMap;
    if (!gd) return preferredClientId || null;

    const entry = findGameEntry(gd, gameId, preferredClientId);
    if (!entry) return preferredClientId || null;
    if (isMembershipDenyingState(entry.state)) return null; // whole-game membership denied

    const clients = entry.clientIdDetailsMap;
    // Owner / game-level grant carries no per-client rows — keep the preferred build.
    if (!clients || Object.keys(clients).length === 0) return preferredClientId || null;

    const usable = Object.entries(clients)
        .filter(([, d]) => !isMembershipDenyingState(d?.state))
        .map(([cid]) => norm(cid));
    if (preferredClientId && usable.includes(norm(preferredClientId))) return norm(preferredClientId);
    return usable[0] || null;
}
